<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { extractLottieAnimation } from './lottieSticker';

const { attachments } = useMessageContext();

const canvas = ref(null);
const hasError = ref(false);
const isLoaded = ref(false);
const attachment = computed(() => attachments.value[0]);

let player;
let requestController;

const loadSticker = async () => {
  requestController = new AbortController();

  try {
    const response = await fetch(attachment.value.dataUrl, {
      signal: requestController.signal,
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to download Lottie sticker');

    const animationData = extractLottieAnimation(await response.arrayBuffer());
    const { DotLottie } = await import('@lottiefiles/dotlottie-web');

    if (!canvas.value || requestController.signal.aborted) return;

    player = new DotLottie({
      canvas: canvas.value,
      data: JSON.stringify(animationData),
      autoplay: true,
      loop: true,
      layout: { fit: 'contain', align: [0.5, 0.5] },
      renderConfig: {
        autoResize: true,
        devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      },
    });
    player.addEventListener('load', () => {
      isLoaded.value = true;
    });
    player.addEventListener('loadError', () => {
      hasError.value = true;
    });
    player.addEventListener('renderError', () => {
      hasError.value = true;
    });
  } catch (error) {
    if (error.name !== 'AbortError') hasError.value = true;
  }
};

onMounted(loadSticker);

onUnmounted(() => {
  requestController?.abort();
  player?.destroy();
});
</script>

<template>
  <BaseBubble
    class="relative overflow-hidden p-3"
    data-bubble-name="lottie-sticker"
  >
    <div
      v-if="hasError"
      class="flex min-h-32 min-w-32 flex-col items-center justify-center gap-2 text-center text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="size-5" />
      <span>{{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}</span>
      <a
        :href="attachment.dataUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="text-n-brand underline"
      >
        {{ $t('CONVERSATION.DOWNLOAD') }}
      </a>
    </div>
    <div v-else class="relative size-48 sm:size-56">
      <div
        v-if="!isLoaded"
        class="absolute inset-0 flex items-center justify-center"
      >
        <Icon icon="i-lucide-loader-circle" class="size-6 animate-spin" />
      </div>
      <canvas
        ref="canvas"
        class="size-full"
        :class="{ 'opacity-0': !isLoaded }"
        width="224"
        height="224"
        role="img"
        :aria-label="$t('COMPONENTS.SLASH_COMMANDS.IMAGE')"
      />
    </div>
  </BaseBubble>
</template>
